import { useState } from 'react';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import EditableImage from '@/components/base/EditableImage';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const galleryImages = Array.from({ length: 16 }).map((_, i) => ({
  id: i + 1,
  src: `https://readdy.ai/api/search-image?query=professional photography gallery image ${i % 5 === 0 ? 'wedding bride portrait' : i % 5 === 1 ? 'romantic couple sunset' : i % 5 === 2 ? 'elegant event details' : i % 5 === 3 ? 'family celebration moment' : 'artistic wedding detail shot'} warm tones professional quality artistic composition&width=500&height=${i % 3 === 0 ? 650 : 500}&seq=${200 + i}&orientation=${i % 3 === 0 ? 'portrait' : 'landscape'}`,
  alt: `Galeria ${i + 1}`,
  category: i % 4 === 0 ? 'Casamentos' : i % 4 === 1 ? 'Pré-Wedding' : i % 4 === 2 ? 'Corporativo' : 'Sessão Família',
}));

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const schema = [
    {
      '@type': 'ImageGallery',
      name: 'Galeria de Fotografia - Jessica Bonini',
      description: 'Explore nossa galeria completa de fotografia profissional. Casamentos, pré-wedding, sessão família e eventos corporativos.',
      url: `${SITE_URL}/galeria`,
      inLanguage: 'pt-BR',
    },
    {
      '@type': 'WebPage',
      name: 'Galeria de Fotografia',
      description: 'Galeria completa de fotos profissionais de casamentos, pré-wedding, sessões familiares e eventos corporativos.',
      url: `${SITE_URL}/galeria`,
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Galeria | Jessica Bonini Fotografia - Casamentos & Sessões"
        description="Explore nossa galeria completa de fotografia profissional. Casamentos, pré-wedding, sessão família e eventos corporativos em Sorocaba e região."
        keywords="galeria fotografia, fotos casamento, fotos pré-wedding, sessão família fotos, portfólio fotográfico"
        canonicalPath="/galeria"
        ogType="website"
        schema={schema}
      />
      <Navbar />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
          <div className="px-6 md:px-10 lg:px-16 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-warm-600 mb-4 block">
              Galeria
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dark font-normal leading-tight mb-6">
              Momentos em <span className="italic">alta definição</span>
            </h1>
            <p className="text-dark/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Explore nossa galeria completa e descubra a arte de eternizar emoções.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 space-y-3 md:space-y-4">
              {galleryImages.map((img) => (
                <div
                  key={img.id}
                  className="group relative break-inside-avoid rounded-xl md:rounded-2xl overflow-hidden cursor-pointer bg-warm-100"
                  onClick={() => setSelectedImage(img.src)}
                >
                  <EditableImage
                    imageKey={`gallery-${img.id}`}
                    src={img.src}
                    alt={img.alt}
                    containerClassName="w-full"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  >
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-dark">
                        <i className="ri-fullscreen-line text-lg" />
                      </span>
                    </div>
                  </EditableImage>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={selectedImage}
            alt="Visualização em tela cheia"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}